import { formatLabel } from './formatLabel.ts';
import type { EnhancedSmartLink, SmartLinkIcon } from './smartLinkTypes.ts';
import { getSmartLinkIcon } from './smartLinkIcon.ts';

const MAX_ICONS = 3;

export type BylineConnectionIcon = {
	key: string;
	label: string;
	icon: SmartLinkIcon;
};

type RankedBylineConnectionIcon = {
	icon: BylineConnectionIcon;
	isUnconnected: boolean;
	occurrenceIndex: number;
};

const isUnconnectedSmartLink = (link: EnhancedSmartLink): boolean => link.access !== 'granted';

const toIcon = (link: EnhancedSmartLink, icon: SmartLinkIcon): BylineConnectionIcon => {
	const product = formatLabel(link.product);

	return {
		key: product || icon.key,
		label: product ?? icon.label ?? '',
		icon,
	};
};

const dedupeIcons = (icons: BylineConnectionIcon[]): BylineConnectionIcon[] => {
	const seenKeys = new Set<string>();
	const dedupedIcons: BylineConnectionIcon[] = [];

	for (const icon of icons) {
		if (!seenKeys.has(icon.key)) {
			seenKeys.add(icon.key);
			dedupedIcons.push(icon);
		}
	}

	return dedupedIcons;
};

const sortRankedIcons = (left: RankedBylineConnectionIcon, right: RankedBylineConnectionIcon) => {
	if (left.isUnconnected !== right.isUnconnected) {
		return Number(right.isUnconnected) - Number(left.isUnconnected);
	}

	return left.occurrenceIndex - right.occurrenceIndex;
};

export const getBylineConnectionIcons = (
	links: EnhancedSmartLink[],
	additionalIcons: BylineConnectionIcon[] = [],
): BylineConnectionIcon[] => {
	const rankedIcons: RankedBylineConnectionIcon[] = [];

	for (const link of links) {
		const icon = getSmartLinkIcon(link);
		if (!icon) {
			continue;
		}

		rankedIcons.push({
			icon: toIcon(link, icon),
			isUnconnected: isUnconnectedSmartLink(link),
			occurrenceIndex: link.occurrenceIndex,
		});
	}

	rankedIcons.sort(sortRankedIcons);
	const sortedIcons = [...additionalIcons, ...rankedIcons.map(({ icon }) => icon)];

	return dedupeIcons(sortedIcons).slice(0, MAX_ICONS);
};
